import { useAuthModalContext, useUserId } from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { Clear } from '@dreamstack/icons/src/icon-components/Clear'
import { Menu } from '@dreamstack/icons/src/icon-components/Menu'
import { RouterOutputs } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import { motion, useAnimation, useAnimationControls } from 'framer-motion'
import map from 'lodash/map'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { forwardRef, useEffect, useState } from 'react'
import { IS_ACCENTRO_WEB } from '../config/config'
import { useGetInvestorsLocaleForLink } from '../hooks/useGetInvestorsLocaleForLink'
import { useMyAccentroNavEntry } from '../hooks/useMyAccentroNavEntry'
import { LocaleSwitcher } from './LocaleSwitcher'
import { MobileEntryWithSubNavbar } from './MobileEntryWithSubNavbar'
import { NavBarLogo } from './NavBarLogo'
import { SimpleMuiButton } from './SimpleMuiButton'

export const NavDivider = () => (
  <motion.div
    variants={MobileNavItemVariants}
    key={'divider'}
    className="h-[2px] bg-accentroWhite-50"
  />
)

export const MobileNavEntriesWrapper = ({
  children,
  animate,
  variants,
}: React.PropsWithChildren<{
  animate?: any
  variants?: any
}>) => (
  <motion.div
    animate={animate}
    variants={variants}
    className="flex flex-col px-px-24 pb-px-32 h-full w-[360px] overflow-y-auto shadow-[inset_0_0_0_9999px_transparent] relative [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
  >
    {children}
  </motion.div>
)

export const StyledMobileLink = forwardRef<
  HTMLAnchorElement,
  { isActive?: boolean; className?: string } & React.PropsWithChildren<
    React.ComponentProps<'a'>
  >
>(({ className, isActive: _isActive, children, ...rest }, ref) => (
  <a
    ref={ref}
    className={cn(
      'font-semibold text-px-16 leading-px-24 border-b-2 border-transparent sm:text-px-20 sm:leading-px-30 group-hover:border-accentroWhite-full hover:border-current',
      className
    )}
    {...rest}
  >
    {children || <span className="sr-only">Navigation link</span>}
  </a>
))

StyledMobileLink.displayName = 'StyledMobileLink'

export const StyledMobileText = ({
  isActive,
  className,
  ...rest
}: {
  isActive?: boolean
  className?: string
} & React.ComponentProps<'span'>) => (
  <span
    className={cn(
      'font-semibold text-px-16 leading-px-24 border-2 border-transparent border-b-transparent hover:border-b-accentroWhite-full sm:text-px-20 sm:leading-px-30',
      isActive && 'border-b-accentroWhite-full border-b-2',
      className
    )}
    {...rest}
  />
)

const MobileNavItemVariants = {
  mobileNavOpen: {
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  mobileNavClosed: {
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
}

export const mobileFadeOutVariants = {
  fadeOut: {
    opacity: 0,
    translateX: '-30px',
    transition: {
      duration: 0.15,
    },
  },
  displayed: {
    translateX: 0,
    opacity: 1,
    transition: {
      duration: 0.15,
    },
  },
}

const MobilNavBar = ({
  children,
  forceDarkTextColor,
  className,
  ...rest
}: React.PropsWithChildren<{
  forceDarkTextColor: boolean
  className?: string
}> &
  React.ComponentProps<typeof motion.div>) => (
  <motion.div
    className={cn(
      'w-full z-10 fixed top-0',
      !forceDarkTextColor && 'text-white',
      className
    )}
    {...rest}
  >
    {children}
  </motion.div>
)

const MobileNavItems = ({
  children,
  animate,
  variants,
}: React.PropsWithChildren<{ animate?: any; variants?: any }>) => (
  <motion.div
    animate={animate}
    variants={variants}
    className="flex flex-col justify-between h-full"
  >
    {children}
  </motion.div>
)

export const MobileNavBar = ({
  forceDark,
  mobileNavOpen,
  setMobileNavOpen,
  entries,
  controls,
  standalone,
}: {
  standalone?: boolean
  forceDark: boolean
  mobileNavOpen: boolean
  entries: RouterOutputs['cms']['getNavEntries']
  setMobileNavOpen: React.Dispatch<React.SetStateAction<boolean>>
  controls: ReturnType<typeof useAnimationControls>
}) => {
  const [fadedOut, setFadedOut] = useState(false)
  const menuControls = useAnimation()
  const { setOpen } = useAuthModalContext()
  const userId = useUserId()

  const myAccentroNavEntry = useMyAccentroNavEntry()

  useEffect(() => {
    setMobileNavOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    if (fadedOut) {
      menuControls.start('fadeOut')
    } else {
      menuControls.start('displayed')
    }
  }, [menuControls, fadedOut])

  useEffect(() => {
    if (!mobileNavOpen) {
      menuControls.start('subNavClosed')
    }
  }, [menuControls, mobileNavOpen])

  const t = useTranslation()
  const locale = useGetInvestorsLocaleForLink()
  let mobileNaviHeight = '100vh'
  if (typeof window !== 'undefined')
    mobileNaviHeight = `${window?.visualViewport?.height}px`

  return (
    <MobilNavBar
      animate={controls}
      transition={{ duration: 0.2 }}
      variants={{
        isAtTop: {
          backgroundColor: '#00000000',
          translateY: 0,
        },
        scrollingDown: {
          translateY: -96,
        },
        scrollingUp: {
          translateY: 0,
          backgroundColor: '#ffffff',
        },
      }}
      forceDarkTextColor={forceDark && !mobileNavOpen}
    >
      <div className="flex top-0 w-full justify-between items-center px-px-24 h-px-96 md:hidden">
        <NavBarLogo forceDarkTextColor={forceDark && !mobileNavOpen} />
        <motion.div className="absolute inset-0 flex justify-end items-center pr-px-24">
          <div
            className="cursor-pointer z-20"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            onKeyPress={() => setMobileNavOpen(!mobileNavOpen)}
            role="button"
            tabIndex={0}
          >
            {mobileNavOpen ? (
              <Clear className="select-none" />
            ) : (
              <Menu className="select-none" />
            )}
          </div>
          <motion.div
            initial={'mobileNavClosed'}
            animate={mobileNavOpen ? 'mobileNavOpen' : 'mobileNavClosed'}
            variants={{
              mobileNavOpen: (height = 1000) => ({
                clipPath: `circle(${height * 2 + 200}px at top right)`,
                transition: {
                  type: 'tween' as const,
                },
                backgroundColor: '#2B2D36',
                height: mobileNaviHeight,
                display: 'flex',
              }),
              mobileNavClosed: {
                clipPath: 'circle(100px at top right)',
                transition: {
                  delay: 0.2,
                  type: 'spring',
                  stiffness: 400,
                  damping: 40,
                },
                backgroundColor: '#2B2D3600',
                height: 'auto',
              },
            }}
            className="absolute text-accentroWhite-full overflow-hidden inset-0 flex pt-px-96 justify-center items-center ml:hidden sm:items-center"
          >
            <MobileNavEntriesWrapper animate={menuControls}>
              <MobileNavItems
                animate={mobileNavOpen ? 'mobileNavOpen' : 'mobileNavClosed'}
                variants={{
                  mobileNavOpen: {
                    transition: { staggerChildren: 0.02, delayChildren: 0.2 },
                  },
                  mobileNavClosed: {
                    transition: { staggerChildren: 0.01, staggerDirection: -1 },
                  },
                }}
              >
                <div className="space-y-px-16 md:space-y-px-24">
                  {!standalone &&
                    map(entries, (entry) => {
                      return (
                        <motion.div
                          variants={MobileNavItemVariants}
                          key={entry.id}
                        >
                          <MobileEntryWithSubNavbar
                            controls={menuControls}
                            setFadedOut={setFadedOut}
                            entry={entry}
                            mobileNavOpen={mobileNavOpen}
                          />
                        </motion.div>
                      )
                    })}
                  {userId && (
                    <motion.div
                      variants={MobileNavItemVariants}
                      key={myAccentroNavEntry.id}
                    >
                      <MobileEntryWithSubNavbar
                        controls={menuControls}
                        setFadedOut={setFadedOut}
                        entry={myAccentroNavEntry}
                        mobileNavOpen={mobileNavOpen}
                      />
                    </motion.div>
                  )}
                  <motion.div
                    animate={menuControls}
                    variants={mobileFadeOutVariants}
                  >
                    <NavDivider />
                  </motion.div>
                  {IS_ACCENTRO_WEB ? (
                    <motion.div
                      key={'investors_nav_item'}
                      variants={MobileNavItemVariants}
                    >
                      <motion.div
                        animate={menuControls}
                        variants={mobileFadeOutVariants}
                      >
                        <Link
                          href={`https://investors.accentro.de/${locale}`}
                          passHref
                          legacyBehavior
                        >
                          <StyledMobileLink target="_blank" isActive={false}>
                            Investor Relations
                          </StyledMobileLink>
                        </Link>
                      </motion.div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={'accentro_nav_item'}
                      variants={MobileNavItemVariants}
                    >
                      <motion.div
                        animate={menuControls}
                        variants={mobileFadeOutVariants}
                      >
                        <Link
                          href={`https://accentro.de/`}
                          passHref
                          legacyBehavior
                        >
                          <StyledMobileLink target="_blank" isActive={false}>
                            ACCENTRO GmbH
                          </StyledMobileLink>
                        </Link>
                      </motion.div>
                    </motion.div>
                  )}
                </div>
                <motion.div
                  className="flex justify-between items-center"
                  variants={MobileNavItemVariants}
                  key={'web'}
                >
                  {IS_ACCENTRO_WEB && (
                    <motion.div variants={MobileNavItemVariants} key={'login'}>
                      <motion.div
                        className="flex justify-between space-x-px-20"
                        animate={menuControls}
                        variants={mobileFadeOutVariants}
                      >
                        {userId ? (
                          <SimpleMuiButton
                            variant="text"
                            color="inherit"
                            onClick={() =>
                              signOut({
                                callbackUrl: `${window.location.origin}`,
                              })
                            }
                          >
                            {t('accentro:logout')}
                          </SimpleMuiButton>
                        ) : (
                          <SimpleMuiButton
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                              setOpen(true)
                            }}
                          >
                            {t('accentro:login')}
                          </SimpleMuiButton>
                        )}
                      </motion.div>
                    </motion.div>
                  )}
                  <motion.div variants={MobileNavItemVariants} key={'language'}>
                    <motion.div
                      animate={menuControls}
                      variants={mobileFadeOutVariants}
                    >
                      <LocaleSwitcher />
                    </motion.div>
                  </motion.div>
                </motion.div>
              </MobileNavItems>
            </MobileNavEntriesWrapper>
          </motion.div>
        </motion.div>
      </div>
    </MobilNavBar>
  )
}
